import { userPath } from '../../shared/paths';

/**
 * Saving the Access & Permissions tab (PD-4070). No matching read: the payload
 * is embedded in the page's props by UsersController#edit, and this answers with
 * the rebuilt table. Thin on purpose — mapping errors onto rows is
 * serverErrors.js's job.
 *
 * The csrf and error helpers below are duplicated from editUserService rather
 * than lifted out of it, since PD-4070 does not rewrite PD-4068's service.
 * Worth collapsing into a shared request module in a follow-up.
 */

// The management layout emits csrf_meta_tags, so the token is on the page.
const csrfToken = () =>
  document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

// The messages a failed response carries, or null if it has none to offer — an
// HTML error page, a proxy timeout or an empty body all land here.
const payloadFrom = async (response) => {
  try {
    const { errors, errorCodes } = await response.json();

    return errors && Object.keys(errors).length ? { errors, errorCodes } : null;
  } catch (error) {
    return null;
  }
};

export const accessPermissionsPath = (id) =>
  `${userPath(id)}/access_permissions`;

/**
 * @returns {Promise<{access?: object, errors?: object, errorCodes?: object}>}
 *   `access` on success — the rebuilt table, which is what the tab and the
 *   header re-render from. `errors` when the server sent messages to show:
 *   a rejected save is an expected outcome of a form, not an exception, and so
 *   is the 403 a viewer without user-management:Modify gets by posting around
 *   the disabled controls. A failure with nothing to say throws.
 */
const saveAccessPermissions = async (id, accounts) => {
  const response = await fetch(accessPermissionsPath(id), {
    method: 'PATCH',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken(),
    },
    body: JSON.stringify({ accounts }),
  });

  if (!response.ok) {
    const payload = await payloadFrom(response);

    if (payload) {
      return payload;
    }

    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const { access } = await response.json();

  return { access };
};

export default saveAccessPermissions;
